/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { auth, db } from "../../firebase";

function toDate(value) {
  try {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "—";
  return `${value}%`;
}

function badgeStyle(status) {
  const s = String(status || "").toLowerCase();

  if (s === "ready") {
    return {
      background: "#DCFCE7",
      color: "#166534",
      border: "1px solid #BBF7D0",
    };
  }

  if (s === "processing") {
    return {
      background: "#FEF3C7",
      color: "#92400E",
      border: "1px solid #FDE68A",
    };
  }

  if (s === "failed") {
    return {
      background: "#FEE2E2",
      color: "#991B1B",
      border: "1px solid #FECACA",
    };
  }

  return {
    background: "#EEF2FF",
    color: "#3730A3",
    border: "1px solid #C7D2FE",
  };
}

export default function MonthlyReportsAdminTable({ schoolId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bootingAuth, setBootingAuth] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  const loadReports = useCallback(async () => {
    if (!schoolId) {
      setRows([]);
      setLoading(false);
      return;
    }

    if (!auth.currentUser) {
      setError("Admin authentication is not ready yet.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const studentsRef = collection(db, "schools", schoolId, "students");
      const studentsSnap = await getDocs(query(studentsRef, orderBy("name")));

      const allRows = [];

      for (const studentDoc of studentsSnap.docs) {
        const studentId = studentDoc.id;
        const student = studentDoc.data() || {};

        const reportsRef = collection(
          db,
          "schools",
          schoolId,
          "students",
          studentId,
          "monthlyReports"
        );

        const reportsSnap = await getDocs(query(reportsRef, orderBy("generatedAt", "desc")));

        reportsSnap.forEach((reportDoc) => {
          const data = reportDoc.data() || {};

          allRows.push({
            id: `${studentId}-${reportDoc.id}`,
            reportId: reportDoc.id,
            studentId,
            studentName:
              student.name ||
              student.fullName ||
              student.displayName ||
              "Unknown child",
            yearGroup:
              student.yearGroup ||
              student.year ||
              student.className ||
              "—",
            monthKey: data.monthKey || reportDoc.id || "",
            monthLabel: data.monthLabel || data.monthKey || reportDoc.id || "—",
            status: data.status || "ready",
            pdfUrl: data.pdfUrl || "",
            storagePath: data.storagePath || "",
            generatedAt: data.generatedAt || null,
            releaseAt: data.releaseAt || null,
            expiresAt: data.expiresAt || null,
            downloadEnabled: !!data.downloadEnabled,
            parentUid: data.parentUid || "",
            summary: data.summary || {},
          });
        });
      }

      allRows.sort((a, b) => {
        const da = toDate(a.generatedAt)?.getTime?.() || 0;
        const dbb = toDate(b.generatedAt)?.getTime?.() || 0;
        return dbb - da;
      });

      setRows(allRows);
    } catch (err) {
      console.error("MonthlyReportsAdminTable load error:", err);
      setError(err?.message || "Failed to load generated monthly reports.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setBootingAuth(false);
      if (!user) {
        setError("You must be signed in as admin to view generated monthly reports.");
        setRows([]);
        setLoading(false);
        return;
      }
      loadReports();
    });

    return () => unsub();
  }, [loadReports, refreshTick]);

  const monthOptions = useMemo(() => {
    return [...new Set(rows.map((r) => r.monthKey).filter(Boolean))].sort((a, b) =>
      a < b ? 1 : -1
    );
  }, [rows]);

  const statusOptions = useMemo(() => {
    return [...new Set(rows.map((r) => String(r.status || "").toLowerCase()).filter(Boolean))];
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = String(search || "").trim().toLowerCase();

    return rows.filter((row) => {
      if (monthFilter && row.monthKey !== monthFilter) return false;
      if (statusFilter && String(row.status || "").toLowerCase() !== statusFilter) return false;

      if (!term) return true;

      return (
        String(row.studentName || "").toLowerCase().includes(term) ||
        String(row.studentId || "").toLowerCase().includes(term) ||
        String(row.monthKey || "").toLowerCase().includes(term) ||
        String(row.monthLabel || "").toLowerCase().includes(term) ||
        String(row.status || "").toLowerCase().includes(term)
      );
    });
  }, [rows, search, monthFilter, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: rows.length,
      ready: rows.filter((r) => String(r.status).toLowerCase() === "ready").length,
      downloadable: rows.filter((r) => !!r.pdfUrl).length,
    };
  }, [rows]);

  return (
    <div style={card}>
      <div style={headerRow}>
        <div>
          <div style={eyebrow}>Parent Reports</div>
          <div style={title}>Generated Monthly Reports</div>
          <div style={subtle}>
            Total: {stats.total} • Ready: {stats.ready} • Downloadable: {stats.downloadable}
          </div>
        </div>

        <button
          type="button"
          style={refreshButton}
          onClick={() => setRefreshTick((v) => v + 1)}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div style={filterGrid}>
        <input
          style={input}
          placeholder="Search by child name, student ID, month or status"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          style={input}
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
        >
          <option value="">All months</option>
          {monthOptions.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <select
          style={input}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error ? <div style={errorBox}>{error}</div> : null}

      {bootingAuth || loading ? (
        <div style={emptyState}>Loading generated monthly reports…</div>
      ) : !filteredRows.length ? (
        <div style={emptyState}>No monthly reports found yet.</div>
      ) : (
        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Child</th>
                <th style={th}>Month</th>
                <th style={th}>Status</th>
                <th style={th}>Attendance</th>
                <th style={th}>Generated</th>
                <th style={th}>Release</th>
                <th style={th}>Expires</th>
                <th style={th}>Download</th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id} style={tr}>
                  <td style={td}>
                    <div style={mainText}>{row.studentName}</div>
                    <div style={subText}>
                      ID: {row.studentId} • {row.yearGroup}
                    </div>
                  </td>

                  <td style={td}>
                    <div style={mainText}>{row.monthLabel}</div>
                    <div style={subText}>{row.monthKey || "—"}</div>
                  </td>

                  <td style={td}>
                    <span style={{ ...badgeBase, ...badgeStyle(row.status) }}>
                      {row.status || "—"}
                    </span>
                  </td>

                  <td style={td}>
                    <div style={mainText}>
                      {formatPercent(row.summary?.attendanceRate)}
                    </div>
                    <div style={subText}>
                      P {row.summary?.present ?? 0} • L {row.summary?.late ?? 0} • A{" "}
                      {row.summary?.absent ?? 0}
                    </div>
                  </td>

                  <td style={td}>{formatDateTime(row.generatedAt)}</td>
                  <td style={td}>{formatDateTime(row.releaseAt)}</td>
                  <td style={td}>{formatDateTime(row.expiresAt)}</td>

                  <td style={td}>
                    {row.pdfUrl ? (
                      <a
                        href={row.pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={downloadBtn}
                      >
                        Download PDF
                      </a>
                    ) : (
                      <span style={subText}>No file</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const card = {
  padding: 18,
  borderRadius: 22,
  background: "#fff",
  border: "1px solid rgba(23,28,58,0.06)",
  boxShadow: "0 10px 30px rgba(24,39,75,0.05)",
  display: "grid",
  gap: 14,
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
};

const eyebrow = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#6d77a0",
  marginBottom: 6,
};

const title = {
  fontSize: 26,
  fontWeight: 900,
  color: "#171c3a",
};

const subtle = {
  color: "#7a849f",
  fontSize: 14,
  lineHeight: 1.5,
};

const filterGrid = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 180px 180px",
  gap: 12,
};

const input = {
  padding: "14px 16px",
  borderRadius: 14,
  border: "1px solid #dbe3f3",
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
};

const refreshButton = {
  padding: "12px 14px",
  borderRadius: 14,
  border: "1px solid #dbe3f3",
  background: "#fff",
  color: "#17203f",
  fontWeight: 800,
  cursor: "pointer",
};

const errorBox = {
  padding: "12px 14px",
  borderRadius: 14,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
};

const emptyState = {
  padding: "18px 16px",
  borderRadius: 16,
  background: "#f8faff",
  color: "#5f6b92",
  border: "1px solid #e4eafb",
};

const tableWrap = {
  overflowX: "auto",
  borderRadius: 16,
  border: "1px solid #edf1fa",
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 1100,
};

const th = {
  textAlign: "left",
  padding: "14px 16px",
  fontSize: 12,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#7180a6",
  background: "#f8faff",
  borderBottom: "1px solid #edf1fa",
  whiteSpace: "nowrap",
};

const tr = {
  borderBottom: "1px solid #edf1fa",
};

const td = {
  padding: "14px 16px",
  color: "#17203f",
  fontSize: 14,
  verticalAlign: "middle",
};

const mainText = {
  fontWeight: 800,
  color: "#17203f",
};

const subText = {
  marginTop: 4,
  fontSize: 12,
  color: "#7a849f",
};

const badgeBase = {
  display: "inline-block",
  padding: "6px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  textTransform: "capitalize",
};

const downloadBtn = {
  display: "inline-block",
  textDecoration: "none",
  background: "#2d2a7f",
  color: "#fff",
  padding: "10px 12px",
  borderRadius: 12,
  fontWeight: 800,
  fontSize: 13,
  whiteSpace: "nowrap",
};